import { HttpClient, HttpErrorResponse, HttpEventType, HttpResponse } from "@angular/common/http";
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { DriverNameComponent } from "@app/directives/_misc/driver-name/driver-name.component";
import { SortType } from "@app/enum/SortType";
import { IPlannedRouteVisit } from "@app/main/modules/tms/_models/IPlannedRouteVisit";
import { FileUploadComponent, IFileUploadDetails } from "@directives/_forms/fileUpload/fileUpload.component";
import { IDriver } from "@interfaces/IDriver";
import { IVehicle } from "@interfaces/IVehicle";
import { ColourService } from "@services/colour.service";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import { IPlannedRoute } from "@tms/_models/IPlannedRoute";
import { PlannedRouteService } from "@tms/_services/planned-route.service";
import { Subscription } from "rxjs";
import { filter, take } from "rxjs/operators";
import "./schedule-import.component.scss";

function selectMany<TIn, TOut>(input: TIn[], selectListFn: (t: TIn) => TOut[]): TOut[] {
    return input.reduce((out, inx) => {
        out.push(...selectListFn(inx));
        return out;
    }, new Array<TOut>());
}

export interface ITMSImportError {
    ErrorType: TMSImportErrorType;
    ErrorLevel: TMSErrorLevel;
    Message: string;
    ColumnName: string;

    ColumnIndex: number;
    RowIndex: number;
    // --

    rowStatus: VTableRowStatus;
    errorLevelString: string;
    errorTypeString: string;
}
export enum TMSImportErrorType {
    ColumnHeaderMapping,
    ColumnHeaderValidation,
    DuplicateColumnHeaderMapping,
    DataParsing,
    DataValidation,
    DataCorrection,
    MultipleDriverMatches,
    DriverNotFound,
    VehicleRegNotFound,
    Information
}

export interface ITMSCsvHeader { }

export interface ITMSFileRecord {
    RouteNumber: number;
    VisitNumber: number;

    Driver: string;
    Address: string;
    Postcode: string;

    VisitArrivalDateTime: string;
    VisitDepartureDateTime: string;
    VisitDuration: string;

    rowStatus: VTableRowStatus;
}

export interface ITMSFileIngestionResult {
    Errors: ITMSImportError[];
    Headers: ITMSCsvHeader[];
    TMSFileRecords: ITMSFileRecord[];
    DriverNameMappings: IDriver[];
    VehicleRegMappings: IVehicle[];
}

export interface ITMSFileParseRequest {
    DateFromInclusive: string;
    Filename: string;
    DefaultGeofenceRadius: number;
}

export interface IGeneratePlannedRoutesModel {
    TMSFileIngestionResult: ITMSFileIngestionResult;
    TMSFileParseRequest: ITMSFileParseRequest;
}

export enum TMSErrorLevel {
    Info,
    Warning,
    Error
}

export enum TMSRouteGenerationErrorType {
    InconsistentRouteField,
    MultipleGeofenceMatches,
    NoGeofenceMatches,
    Exception,
    InvalidInput,
    DuplicateReference,
    OverlappingRoute,
    NoVisits,
    GeocodingFailed,
    GeofenceRadiusTooSmall,
    Unauthorised
}

export interface ITMSRouteGenerationError {
    ErrorType: TMSRouteGenerationErrorType;
    Message: string;
    ColumnName: string;
    ErrorLevel: TMSErrorLevel;
    // ---

    errorStatus: VTableRowStatus;
}

export interface IGeneratedRouteSummary {

    validRoutes: ITMSPlannedRouteGenerationResult[];
    invalidRoutes: ITMSPlannedRouteGenerationResult[];

    allRoutes: ITMSPlannedRouteGenerationResult[];
}

export interface ITMSPlannedRouteGenerationResult {
    Errors: ITMSRouteGenerationError[];
    PlannedRoute: IPlannedRoute;
    Success: boolean;
    RouteNumber: number;
    // ---

    uploaded: boolean;
    rowStatus: VTableRowStatus;
    name: string;
    errorString: string;
}

interface IValidationSummaryBox {
    count: number;
    label: string;
    icon: string;
    color?: string;
}

@Component({
    selector: 'schedule-import',
    template: require('./schedule-import.component.html')
})
export class ScheduleImportComponent implements OnInit, AfterViewInit, OnDestroy {
    loading: boolean;

    importString: string;
    importFilename: string;

    validationResult: ITMSFileIngestionResult;
    validRowCount: number;

    fileResultSummary: IValidationSummaryBox[];
    routeResultSummary: IValidationSummaryBox[];

    generatedRouteSummary: IGeneratedRouteSummary;
    selectedGeneratedRoute: ITMSPlannedRouteGenerationResult;

    onFinishRedirectTo = "app.tms.scheduling";

    generateRoutesSub: Subscription;

    numToSave = 0;
    saveSuccessCount = 0;
    saveErrorCount = 0;
    readonly exampleCsvFilePath = "/assets/files/ExampleRouteImportFile.csv";
    
    @ViewChild(FileUploadComponent) fileUpload: FileUploadComponent;
    @ViewChild('weekCommencingDate') weekCommencingDate: ElementRef<HTMLInputElement>;
    @ViewChild('defaultArea') defaultAreaInput: ElementRef<HTMLInputElement>;

    startDateValid: boolean = true;
    dateFromInclusive: string;

    defaultAreaValid: boolean = true;
    defaultGeofenceRadius: number = 250;

    validationErrorsTableColumns: IVTableColumn<ITMSImportError>[];
    validationErrorsTableRows: IVTableRow[];
    fileRecordsTableColumns: IVTableColumn<ITMSFileRecord>[];
    fileRecordsTableRows: IVTableRow[];
    allRoutesWithErrorsTableColumns: IVTableColumn<ITMSPlannedRouteGenerationResult>[];
    allRoutesTableColumns: IVTableColumn<ITMSPlannedRouteGenerationResult>[];
    allRoutesTableRows: IVTableRow[];

    selectedGeneratedRouteColumns: IVTableColumn<IPlannedRouteVisit>[];
    selectedGeneratedRouteRows: IVTableRow[];
    selectedRouteErrorColumns: IVTableColumn<ITMSRouteGenerationError>[]
    selectedRouteErrorRows: IVTableRow[];

    constructor(
        private readonly http: HttpClient,
        private readonly colourService: ColourService,
        private readonly plannedRouteService: PlannedRouteService,
        private readonly translationService: TranslationService,
        private readonly veriDate: VeriDate,
        private readonly vTableService: VTableService
    ) {
    }
    ngOnDestroy(): void {
        if (this.generateRoutesSub) {
            this.generateRoutesSub.unsubscribe();
        }
    }

    ngOnInit(): void {
        this.createTables();
    }

    ngAfterViewInit(): void {
        let today = this.veriDate.getDateInInputFormat(new Date());
        this.weekCommencingDate.nativeElement.value = this.weekCommencingDate.nativeElement.min = today;
        this.defaultAreaInput.nativeElement.value = "250";
    }

    onRouteRowClicked(row: IVTableRow): void {
        this.selectedGeneratedRoute = row.data;
        this.createSelectedRouteTables();
    }

    onImportFileChanged(event: IFileUploadDetails) {
        if (this.weekCommencingDate.nativeElement.checkValidity()) {
            this.uploadCSV(event.file);
            this.startDateValid = true;

        } else {
            this.fileUpload.hasFile = false;
            this.startDateValid = false;
        }
    }

    onDateChange(event: Event) {
        if (!this.weekCommencingDate.nativeElement.checkValidity()) {
            this.startDateValid = false;
        } else {
            this.startDateValid = true;
            this.dateFromInclusive = this.weekCommencingDate.nativeElement.value;
        }
        if (this.startDateValid && this.fileUpload.hasFile) {
            this.uploadCSV(this.fileUpload.file);
        }
    }

    onDefaultAreaChange() {
        if (!this.defaultAreaInput.nativeElement.checkValidity()) {
            this.defaultAreaValid = false;
        } else {
            this.defaultAreaValid = true;
            this.defaultGeofenceRadius = Number.parseInt(this.defaultAreaInput.nativeElement.value);
        }
    }

    uploadCSV(file: File) {
        this.reset();

        const formData = new FormData();
        formData.append('file', file, file.name);
        this.importFilename = file.name;

        this.dateFromInclusive = this.weekCommencingDate.nativeElement.value;
        this.uploadRouteCsv(formData);
    }

    downloadExampleFile() {
        window.location.assign(this.exampleCsvFilePath);
    }


    savePlannedRoutes() {

        this.numToSave = 0;
        this.saveSuccessCount = 0;
        this.saveErrorCount = 0;
        this.loading = true;

        this.allRoutesTableRows = [];
        this.allRoutesTableColumns = this.createAllRouteTableColumns(false);
        /* Code for using addRoutes - WIP */
        //this.generatedRouteSummary.allRoutes.forEach((route, i) => {
        //    if (!route.Success) {
        //        return;
        //    }
        //    route.rowStatus = VTableCellStatus.Pending;
        //})

        //const allRoutes = this.generatedRouteSummary.allRoutes.map(route => route.Success && route.PlannedRoute);
        //this.plannedRouteService.addPlannedRoutes(allRoutes).then(addedRoutes => {

        //    this.saveSuccessCount = addedRoutes.filter(addedRoute => !addedRoute.IsError).length;
        //    this.saveErrorCount = addedRoutes.filter(addedRoute => addedRoute.IsError).length;

        //    this.generatedRouteSummary.allRoutes.forEach((route, i) => {

        //        if (!route.Success) {
        //            return;
        //        }

        //        const matchingRoute = addedRoutes.find(returnedRoute => returnedRoute.Value?.Id === route.PlannedRoute.Id);

        //        if (!matchingRoute.IsError) {
        //            route.rowStatus = VTableCellStatus.Success;
        //        }
        //        else {
        //            route.rowStatus = VTableCellStatus.Error;
        //        }
        //        this.table.updateRow(route);
        //    })
        //}).finally(() => {
        //    this.loading = false;
        //})
        /* End code for using addRoutes - WIP */

        this.generatedRouteSummary.allRoutes.forEach((route, i) => {
            if (!route.Success) {
                return;
            }

            this.numToSave++;
            route.rowStatus = VTableRowStatus.Pending;

            this.plannedRouteService.addPlannedRoute(route.PlannedRoute)
                .then(() => {
                    route.rowStatus = VTableRowStatus.Success;
                    this.saveSuccessCount++;
                })
                .catch(() => {
                    route.rowStatus = VTableRowStatus.Error;
                    this.saveErrorCount++;
                }).finally(() => {

                    this.allRoutesTableRows = this.vTableService.createTableRows(this.generatedRouteSummary.allRoutes, this.allRoutesTableColumns)

                    this.loading = this.saveSuccessCount + this.saveErrorCount != this.numToSave;
                });
        });

    }

    // ---

    private createSummaryBox(count: number, icon: string, label: string, color: string): IValidationSummaryBox {
        return {
            count: count,
            icon: icon, //validate
            label: label,
            color: color
        };
    }

    private processValidationResult() {
        this.fileResultSummary = [];

        const rowsWithErrors = this.validationResult.Errors.filter(e => e.ErrorLevel === TMSErrorLevel.Error).map(e => e.RowIndex).filter(e => e !== 0); // We do not count row 0 as that is column headers
        const errorCount = [...new Set(rowsWithErrors)].length; // Gets unique row indexes for validRowCount
        this.validRowCount = this.validationResult.TMSFileRecords.length - errorCount;

        this.fileResultSummary.push(this.createSummaryBox(this.validRowCount, "done", "Valid Rows", this.colourService.greenColor));
        this.fileResultSummary.push(this.createSummaryBox(this.validationResult.Errors.map(e => e.ErrorLevel).filter(e => e == TMSErrorLevel.Warning).length, "warning", "Warnings", this.colourService.orangeColor));
        this.fileResultSummary.push(this.createSummaryBox(this.validationResult.Errors.map(e => e.ErrorLevel).filter(e => e == TMSErrorLevel.Error).length, "clear", "Errors", this.colourService.redColor));

        this.fileResultSummary.forEach(rs => {
            if (rs.count == 0) {
                rs.color = this.colourService.greyColor;
            }
        });

        // Set all results to good, before we test for errors
        this.validationResult.TMSFileRecords.forEach(fr => { fr.rowStatus = VTableRowStatus.Success; });

        this.validationResult.Errors.forEach(error => {
            error.rowStatus = this.convertTmsErrorLevelToTableCellStatus(error.ErrorLevel);
            error.errorLevelString = TMSErrorLevel[error.ErrorLevel];
            error.errorTypeString = TMSImportErrorType[error.ErrorType];

            // Find matching status
            // 0 is actually the header row, we  should ignore 0 and subtract one
            error.RowIndex--;
            if (error.RowIndex >= 0) {
                this.validationResult.TMSFileRecords[error.RowIndex].rowStatus = error.rowStatus;
            }
        });
    }

    private uploadRouteCsv(formData: FormData) {
        formData.append("DateFromInclusive", this.dateFromInclusive);
        formData.append("Filename", this.importFilename);
        formData.append("DefaultGeofenceRadius", this.defaultGeofenceRadius.toString());

        this.loading = true;
        this.http.post(`/api/routeimport`, formData, { reportProgress: true, observe: 'events' })
            .subscribe({
                next: (event) => {
                    if (event.type === HttpEventType.UploadProgress) {
                        //console.log("Upload progress", Math.round(100 * event.loaded / event.total));
                    }
                    else if (event.type === HttpEventType.Response) {
                        this.validationResult = event.body as ITMSFileIngestionResult;
                        this.processValidationResult();

                        this.validationErrorsTableRows = this.vTableService.createTableRows(this.validationResult.Errors, this.validationErrorsTableColumns);

                        this.fileRecordsTableRows = this.vTableService.createTableRows(this.validationResult.TMSFileRecords, this.fileRecordsTableColumns);
                    }
                },
                error: (err: HttpErrorResponse) => console.error(err),
                complete: () => this.loading = false
            });
    }

    private generatePlannedRoutes() {
        // Dont rerun this unless required
        if (this.generatedRouteSummary) {
            return;
        }

        const generatePlannedRoutesModel: IGeneratePlannedRoutesModel =
        {
            TMSFileIngestionResult: this.validationResult,
            TMSFileParseRequest: {
                DateFromInclusive: this.dateFromInclusive,
                Filename: this.importFilename,
                DefaultGeofenceRadius: this.defaultGeofenceRadius,
            },
        };

        this.loading = true;
        this.generateRoutesSub = this.http.post(`/api/routeimport/generateroutes`, generatePlannedRoutesModel, { reportProgress: true, observe: 'events' })
            .pipe(
                filter(event => event.type === HttpEventType.Response),
                take(1)
            )
            .subscribe({
                next: (event: HttpResponse<ITMSPlannedRouteGenerationResult[]>) => {
                    this.generatedRouteSummary = {
                        allRoutes: event.body as ITMSPlannedRouteGenerationResult[],
                        invalidRoutes: [],
                        validRoutes: []
                    };
                    this.processPlannedRoutes();
                    this.allRoutesTableRows = this.vTableService.createTableRows(this.generatedRouteSummary.allRoutes, this.allRoutesWithErrorsTableColumns);
                },
                error: (err: HttpErrorResponse) => {
                    console.error(err)
                },
                complete: () => {
                    this.loading = false;
                }
            });
    }

    private convertTmsErrorLevelToTableCellStatus(maxTmsErrorLevel: TMSErrorLevel): VTableRowStatus {
        switch (maxTmsErrorLevel) {
            case TMSErrorLevel.Info:
                return VTableRowStatus.Info;

            case TMSErrorLevel.Warning:
                return VTableRowStatus.Warning;

            case TMSErrorLevel.Error:
                return VTableRowStatus.Error;
        }
    }

    private processPlannedRoutes() {
        this.generatedRouteSummary.allRoutes.forEach(r => {
            r.name = r.PlannedRoute.PlannedVisits[0]?.Name || 'None';
            if (r.Errors.length == 0) {
                r.rowStatus = VTableRowStatus.Success;
            }
            else {

                r.Errors?.forEach(error => {
                    error.errorStatus = this.convertTmsErrorLevelToTableCellStatus(error.ErrorLevel);
                });
                const maxTmsErrorLevel = Math.max(...r.Errors.map(e => e.ErrorLevel));
                const highPriorityError = r.Errors.find(e => e.ErrorLevel === maxTmsErrorLevel);

                if (r.Errors.length === 1) {
                    r.errorString = r.Errors[0].Message.substr(0, 50).trim() + '...';
                }
                else if (r.Errors.length > 1) {
                    const errorCount = r.Errors.length - 1;
                    r.errorString = `${highPriorityError.Message.substr(0, 50).trim()}... (+${errorCount} ${this.translationService.t('ScheduleImport.OtherErrors')})`;
                }
                r.rowStatus = this.convertTmsErrorLevelToTableCellStatus(maxTmsErrorLevel);
            }
        });

        this.generatedRouteSummary.validRoutes = this.generatedRouteSummary.allRoutes.filter(r => r.Success);
        this.generatedRouteSummary.invalidRoutes = this.generatedRouteSummary.allRoutes.filter(r => !r.Success);

        this.routeResultSummary = [];

        this.routeResultSummary.push(this.createSummaryBox(this.generatedRouteSummary.validRoutes.length, "done", "Valid Routes", this.colourService.greenColor));
        this.routeResultSummary.push(this.createSummaryBox(this.generatedRouteSummary.invalidRoutes.length, "clear", "Invalid Routes", this.colourService.redColor));
    }

    private reset() {
        this.validRowCount = 0;
        this.validationResult = null;

        this.fileResultSummary = [];
        this.routeResultSummary = [];

        this.generatedRouteSummary = null;
        this.selectedGeneratedRoute = null;
    }

    private createTables(): void {
        this.createValidationErrorsTableColumns();
        this.createFileRecordTableColumns();
        this.allRoutesWithErrorsTableColumns = this.createAllRouteTableColumns(true);
    }

    private createValidationErrorsTableColumns(): void {
        this.validationErrorsTableColumns = [
            {
                header: this.vTableService.createStatusTableHeader({ tKey: 'ScheduleImport.Status', width: 90 }),
                createCellFunction: error => this.vTableService.createStatusCell(error.rowStatus)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'ScheduleImport.Row', width: 60, sortType: SortType.Number }),
                createCellFunction: error => this.vTableService.createTableCell(error.RowIndex)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'ScheduleImport.Column', width: 60, sortType: SortType.Number }),
                createCellFunction: error => this.vTableService.createTableCell(error.ColumnIndex)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'ScheduleImport.Error', width: 180 }),
                createCellFunction: error => this.vTableService.createTableCell(error.errorTypeString)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'ScheduleImport.ColumnName', width: 180 }),
                createCellFunction: error => this.vTableService.createTableCell(error.ColumnName)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'ScheduleImport.Details' }),
                createCellFunction: error => this.vTableService.createTableCell(error.Message)
            }
        ]
    }

    private createFileRecordTableColumns(): void {
        this.fileRecordsTableColumns = [
            {
                header: this.vTableService.createStatusTableHeader({ tKey: 'ScheduleImport.Status', width: 90 }),
                createCellFunction: record => this.vTableService.createStatusCell(record.rowStatus)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Route', width: 80, sortType: SortType.Number }),
                createCellFunction: record => this.vTableService.createTableCell(record.RouteNumber, { classes: "text-right" })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Tms.Visit', width: 80, sortType: SortType.Number }),
                createCellFunction: record => this.vTableService.createTableCell(record.VisitNumber, { classes: "text-right" })
            },
            {
                header: this.vTableService.createDateTableHeader(),
                createCellFunction: record => this.vTableService.createDateCell(record.VisitArrivalDateTime)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Driver', sortType: SortType.Number }),
                createCellFunction: record => this.vTableService.createTableCell(record.Driver)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Address', sortType: SortType.Number }),
                createCellFunction: record => this.vTableService.createTableCell(record.Address)
            }
        ]
    }

    private createAllRouteTableColumns(withErrors: boolean): IVTableColumn<ITMSPlannedRouteGenerationResult>[] {
        const columns = [
            {
                header: this.vTableService.createStatusTableHeader({ tKey: 'ScheduleImport.Status' }),
                createCellFunction: route => this.vTableService.createStatusCell(route.rowStatus)
            },

            {
                header: this.vTableService.createDateTableHeader({ tKey: 'ScheduleImport.StartTime' }),
                createCellFunction: route => this.vTableService.createDateCell(route.PlannedRoute.PlannedStartTime)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'ScheduleImport.FirstVisit', width: 250 }),
                createCellFunction: route => this.vTableService.createTableCell(route.name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Driver', sortable: false }),
                createCellFunction: route => this.vTableService.createTableCell(route.PlannedRoute?.DriverId, {
                    component: DriverNameComponent,
                    componentInputs: { id: route.PlannedRoute?.DriverId }
                })
            }
        ]

        if (withErrors) {
            columns.splice(1, 0,
                {
                    header: this.vTableService.createTableHeader({ tKey: 'Common.Route', width: 80, sortType: SortType.Number }),
                    createCellFunction: route => this.vTableService.createTableCell(route.RouteNumber, { classes: "text-right" })
                },);

            columns.push(
                {
                    header: this.vTableService.createTableHeader({ tKey: 'ScheduleImport.Errors' }),
                    createCellFunction: route => this.vTableService.createTableCell(route.errorString)
                }
            )
        }

        return columns;
    }

    private createSelectedRouteTables(): void {
        this.createSelectedColumns();
        this.createSelectedRouteRows();
    }

    private createSelectedColumns(): void {
        this.selectedGeneratedRouteColumns = [
            {
                header: this.vTableService.createDateTableHeader({ tKey: 'ScheduleImport.PlannedArrival', sorted: true }),
                createCellFunction: visit => this.vTableService.createDateCell(visit.PlannedArrivalTime)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: 'ScheduleImport.PlannedDeparture' }),
                createCellFunction: visit => this.vTableService.createDateCell(visit.PlannedDepartureTime)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'ScheduleImport.Location' }),
                createCellFunction: visit => this.vTableService.createTableCell(visit.Name)
            }
        ];

        this.selectedRouteErrorColumns = [
            {
                header: this.vTableService.createStatusTableHeader({ tKey: 'ScheduleImport.Status' }),
                createCellFunction: error => this.vTableService.createStatusCell(error.errorStatus)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'ScheduleImport.Errors' }),
                createCellFunction: error => this.vTableService.createTableCell(error.Message)
            }
        ]
    }

    private createSelectedRouteRows(): void {
        this.selectedGeneratedRouteRows = this.vTableService.createTableRows(this.selectedGeneratedRoute.PlannedRoute.PlannedVisits, this.selectedGeneratedRouteColumns);

        this.selectedRouteErrorRows = this.vTableService.createTableRows(this.selectedGeneratedRoute.Errors, this.selectedRouteErrorColumns);
    }
}
